import { absoluteUrl } from '@/lib/utils'

export const siteConfig = {
  name: 'Euclideum Solutions',

  /**
   * Kept under ~160 characters so Google renders it whole, and led with the
   * terms the site actually competes for ("campus ERP software", "colleges",
   * "universities") rather than abstract positioning language.
   */
  description:
    'Campus ERP software for schools, colleges and universities. Manage admissions, examinations, fees, HRMS, payroll and LMS in one campus management system.',

  /**
   * Non-www is the canonical host: every URL Google has indexed and ranks is
   * non-www, while this config previously declared www — so canonicals and the
   * sitemap pointed at a hostname that had no accumulated authority. www now
   * 301s here (see next.config.js).
   */
  url: 'https://euclideum.com',

  og: {
    image: absoluteUrl('/og.jpg'),

    size: {
      width: 1200,
      height: 630,
    },
  },

  app: {
    latestVersion: '1.0.0',
  },

  company: {
    name: 'Euclideum Solutions Private Limited',
    legalName: 'Euclideum Solutions Private Limited',
    tagline: 'Shaping the Future Through Innovation',
    email: 'contact@euclideum.com',
    // The number already published on /support and every service-location page.
    phone: '+919930202329',
    address: {
      state: 'Maharashtra',
      country: 'IN',
    },
  },

  links: {
    twitter: {
      label: 'Twitter',
      username: '@euclideum',
      url: 'https://twitter.com/euclideum',
    },

    linkedin: {
      label: 'LinkedIn',
      url: 'https://linkedin.com/company/euclideum-solutions',
    },

    // Both of these 404 as of the last check, so they are excluded from the
    // schema.org sameAs graph in app/layout.tsx and the docs "edit this page"
    // links are hidden (see components/docs/toc.tsx). Set `exists: true` once a
    // real profile is in place and both will light up again.
    github: {
      label: 'GitHub',
      url: 'https://github.com/euclideum',
      exists: false,
    },

    youtube: {
      label: 'YouTube',
      url: 'https://youtube.com/@euclideum',
      exists: false,
    },
  },

  industries: [
    'Campus ERP Software',
    'School Management Software',
    'Higher Education ERP',
    'Learning Management Systems',
    'HRMS and Payroll Software',
    'Cloud Infrastructure',
  ],

  /**
   * Search Console shows the site already earns impressions for every term
   * below while ranking positions 75–100, i.e. the relevance is recognised but
   * the pages are too thin to place. These are the terms page copy, titles and
   * descriptions are written against — not aspirational branding language.
   *
   * The second block is volume-validated (India, Aug 2026): "school management
   * system" 12,100/mo KD 23, "school management software" 3,600/mo KD 30,
   * "erp for schools" 2,900/mo KD 37, "student information system" 1,600/mo
   * KD 7, "campus erp" 1,300/mo KD 5, plus a KD ≤ 12 tail ("academic erp",
   * "education erp", "university erp") and "campus management system"
   * (110,000/mo at KD 1, navigational).
   */
  keywords: [
    'campus erp software',
    'college erp software',
    'school erp software',
    'school erp solution',
    'student erp system',
    'college erp system',
    'erp software for college',
    'higher education erp software',
    'erp solutions for universities',
    'erp campus management',
    'unified campus management solution',
    'education management solution',
    'curriculum management solution',
    'student information system',
    'hrms cloud solutions',
    'school management system',
    'school management software',
    'erp for schools',
    'campus management system',
    'campus erp',
    'education erp',
    'academic erp',
    'university erp',
    'lms for schools',
    'euclideum',
    'euclideum solutions',
  ],

  /**
   * Spelling variants Google currently serves this site for, drawn from live
   * Search Console queries. They are emitted as schema.org `alternateName` so
   * the entity resolves to Euclideum instead of being blended with the
   * unrelated "Euclid"-branded companies competing for the same impressions.
   */
  brandVariants: [
    'Euclideum',
    'Euclideum Solutions',
    'Euclideum Solutions Pvt Ltd',
    'Euclidium',
    'Euclideum Software Solutions',
  ],
} as const

export type SiteConfig = typeof siteConfig
